import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import TinyGallery from '../components/TinyGallery';
import L from 'leaflet';
import styled from 'styled-components';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TrackingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f7fafc 25%, #edf2f7 50%, #e2e8f0 75%, #f8fafc 100%);
  padding: 6rem 2rem 4rem 2rem;
  color: #1a365d;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem 2rem 1rem;
  }
`;

const BackgroundEffects = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(66, 153, 225, 0.08) 0%, transparent 50%), 
              radial-gradient(circle at 80% 20%, rgba(144, 205, 244, 0.05) 0%, transparent 50%);
  pointer-events: none;
`;

const Title = styled.h1`
  text-align: center;
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 300;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a365d, #3182ce, #4299e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(66, 153, 225, 0.3);
  letter-spacing: 2px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #718096;
  font-size: clamp(1rem, 3vw, 1.2rem);
  margin-bottom: 2rem;
  font-weight: 300;
  letter-spacing: 1px;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const AISStatus = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(66, 153, 225, 0.1);
  border-radius: 15px;
  max-width: 800px;
  margin: 0 auto 2rem auto;
  border: 1px solid rgba(66, 153, 225, 0.2);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    margin: 0 1rem 1.5rem 1rem;
    padding: 0.75rem;
    font-size: 0.9rem;
  }
`;

const MapWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 25px 50px rgba(66, 153, 225, 0.15);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    margin: 0 1rem;
    padding: 1rem;
    border-radius: 15px;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(66, 153, 225, 0.05) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%);
  pointer-events: none;
`;

const StageButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    gap: 0.75rem;
    margin: 1.5rem 0;
  }

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin: 1rem 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    max-width: 400px;
    margin: 1rem auto;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    max-width: 200px;
  }
`;

const StageButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #3182ce, #4299e1)' 
    : 'linear-gradient(135deg, rgba(66, 153, 225, 0.1), rgba(255, 255, 255, 0.8))'};
  color: ${props => props.isActive ? '#ffffff' : '#3182ce'};
  border: 1px solid ${props => props.isActive ? '#3182ce' : 'rgba(66, 153, 225, 0.3)'};
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  min-width: 140px;
  text-align: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 1024px) {
    min-width: 120px;
    padding: 0.7rem 1.25rem;
    font-size: 0.85rem;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    min-width: auto;
    width: 100%;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.9rem;
    border-radius: 20px;
  }
`;

const MapContainer_ = styled.div`
  height: 600px;
  border-radius: 15px;
  overflow: hidden;
  border: 2px solid rgba(66, 153, 225, 0.3);
  position: relative;
  z-index: 1;
  background: #0d1421;

  @media (max-width: 1024px) {
    height: 500px;
  }

  @media (max-width: 768px) {
    height: 400px;
    border-radius: 12px;
    border-width: 1px;
  }

  @media (max-width: 480px) {
    height: 350px;
    border-radius: 10px;
  }
`;

const InfoPanel = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(66, 153, 225, 0.08));
  border: 1px solid rgba(66, 153, 225, 0.2);
  color: #1a365d;
  padding: 2rem;
  border-radius: 15px;
  margin-top: 2rem;
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 1;
  box-shadow: 0 8px 32px rgba(66, 153, 225, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3182ce, transparent);
    border-radius: 15px 15px 0 0;
  }

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 1.5rem;
    border-radius: 12px;
    font-size: 0.9rem;
    
    &::before {
      border-radius: 12px 12px 0 0;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1rem;
    border-radius: 10px;
    font-size: 0.85rem;
    
    &::before {
      border-radius: 10px 10px 0 0;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-top: 1rem;
  }
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, rgba(66, 153, 225, 0.08), rgba(255, 255, 255, 0.8));
  padding: 1.25rem;
  border-radius: 10px;
  text-align: center;
  border: 1px solid rgba(66, 153, 225, 0.3);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(66, 153, 225, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 6px;
  }
`;

const InfoCardLabel = styled.strong`
  color: #3182ce;
  display: block;
  margin-bottom: 0.5rem;
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    margin-bottom: 0.4rem;
  }
`;

const InfoCardValue = styled.p`
  color: #4a5568;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  font-weight: 300;
  margin: 0;
  line-height: 1.4;
`;

interface AISData {
  latitude: number;
  longitude: number;
  speed: number;
  course: number;
  timestamp: string;
  status: string;
  mmsi?: number;
  shipName?: string;
}

const useRealAISData = () => {
  const [aisData, setAisData] = useState<AISData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const apiKey = 'ec8605e2506f7eef9cf4104f4ca5aa7284d850a4';
    const targetMMSI = 275049164;
    let isMounted = true;

    const connectWebSocket = () => {
      const socket = new WebSocket('wss://stream.aisstream.io/v0/stream');
      wsRef.current = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify({
          APIKey: apiKey,
          BoundingBoxes: [[[-90, -180], [90, 180]]],
          FiltersShipMMSI: [String(targetMMSI)],
          FilterMessageTypes: ['PositionReport'],
        }));
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (
            msg.MessageType === 'PositionReport' &&
            msg.MetaData?.MMSI === targetMMSI
          ) {
            const rpt = msg.Message.PositionReport;
            if (!isMounted) return;

            setAisData({
              mmsi: msg.MetaData.MMSI,
              shipName: msg.MetaData.ShipName,
              latitude: rpt.Latitude,
              longitude: rpt.Longitude,
              speed: rpt.Sog ?? 0,
              course: rpt.Cog ?? 0,
              timestamp: msg.MetaData.time_utc,
              status: 'Under way',
            });
            setError(null);
            setIsLoading(false);
          }
        } catch (e) {
          console.error('WS parse error', e);
        }
      };

      socket.onerror = (e) => {
        console.error('WebSocket error', e);
        if (isMounted) setError('Ошибка WebSocket');
      };

      socket.onclose = () => {
        if (isMounted) {
          setError('Соединение закрыто, переподключаюсь…');
          setTimeout(connectWebSocket, 5000);
        }
      };
    };

    const fetchMarineTraffic = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const res = await fetch(
          `https://services.marinetraffic.com/api/exportvessel/v:3/${apiKey}/mmsi:${targetMMSI}/protocol:jsono`
        );
        if (!res.ok) throw new Error('MT response not OK');

        const arr = await res.json();
        if (Array.isArray(arr) && arr.length > 0) {
          const v = arr[0];
          if (!isMounted) return true;

          setAisData({
            mmsi: Number(v.MMSI),
            shipName: v.SHIPNAME || 'ARCTIC OWL',
            latitude: parseFloat(v.LAT),
            longitude: parseFloat(v.LON),
            speed: parseFloat(v.SPEED) || 0,
            course: parseFloat(v.COURSE) || 0,
            timestamp: v.TIMESTAMP,
            status: v.STATUS || 'Under way',
          });
          setError(null);
          setIsLoading(false);
          return true;
        }
      } catch (err) {
        console.log('MarineTraffic fail:', err);
      }
      return false;
    };

    (async () => {
      const gotMt = await fetchMarineTraffic();
      if (!gotMt && isMounted) {
        connectWebSocket();
      }
    })();

    return () => {
      isMounted = false;
      wsRef.current?.close();
    };
  }, []);

  return { aisData, isLoading, error };
};

const Tracking: React.FC = () => {
  const [currentStage, setCurrentStage] = useState(1);
  const { aisData, isLoading, error } = useRealAISData();
  
  const stages = [
    {
      id: 1,
      title: 'Qualification',
      description: 'Deliver the boat to Las Palmas as a qualification for ARC',
      route: 'Spain → Gibraltar → Gran Canaria',
      duration: '10-14 days at sea',
      distance: '1,100 nautical miles'
    },
    {
      id: 2,
      title: 'ARC Rally',
      description: 'The legendary Atlantic Rally for Cruisers from Las Palmas de Gran Canaria to St.Lucia(Caribbean)',
      route: 'Gran Canaria → St.Lucia(Caribbean)',
      duration: '18-24 days at sea',
      distance: '2,700 nautical miles'
    },
    {
      id: 3,
      title: 'Carribean paradise and Panama crossing',
      description: 'Navigate through the Carribean and go through the Panama Channel on yor own.',
      route: 'St.Lucia(Caribbean) → Panama Canal',
      duration: '2 weeks',
      distance: '1,300 nautical miles'
    },
    {
      id: 4,
      title: 'Pacific Crossing',
      description: 'Go along the North American West coast collecting all the biggest cities on the way.',
      route: 'Panama Canal → Acapulco → LosAngeles → Seattle',
      duration: '6-8 weeks',
      distance: '4,800 nautical miles'
    },
    {
      id: 5,
      title: 'Northwest Passage',
      description: 'The legendary Arctic route through Alaska, Bering Strait, and Canadian Arctic waters to Greenland.',
      route: 'Seattle → Alaska → Bering Strait → Arctic Canada → Greenland',
      duration: '3-4 months',
      distance: '3,200 nautical miles'
    },
    {
      id: 6,
      title: 'Atlantic Return',
      description: 'The final leg home across the North Atlantic from Greenland back to Spain.',
      route: 'Greenland → Faroe Islands → Spain',
      duration: '3-4 weeks',
      distance: '2,500 nautical miles'
    }
  ];

  // Sea routes (typed for Leaflet) - Enhanced with more waypoints
  const seaRoutes: { [key: string]: [number, number][] } = {
    stage1: [
      [38.0, -0.7], // Cabo Roig, Spain
      [37.0, -2.0], [36.0, -3.5], [35.5, -5.2], [34.0, -7.0], [32.0, -9.0],
      [30.5, -11.0], [29.0, -13.0], [28.1, -15.4], // Las Palmas, Gran Canaria
    ],
    stage2: [
      [27.0, -17.0], [25.5, -20.0], [24.0, -23.0], [22.0, -27.0], [20.0, -31.0],
      [18.5, -36.0], [17.0, -42.0], [16.0, -48.0], [15.5, -54.0], [15.0, -58.0], [14.7, -61.2], // Caribbean
    ],
    stage3: [
      [14.7, -61.2], // Caribbean start
      [13.5, -65.0], [12.0, -68.5], [11.0, -72.0], [10.5, -75.5], [10.0, -78.0],
      [9.5, -79.5], // Panama Canal approach
      [9.0, -79.8], [8.8, -79.9], [9.0, -80.0], [9.2, -80.1], // Through Panama Canal
    ],
    stage4: [
      [10.0, -82.0], [12.0, -85.0], [15.0, -88.0], [18.0, -92.0], [20.0, -96.0],
      [23.0, -100.0], [26.0, -105.0], [29.0, -110.0], [32.0, -115.0], [35.0, -118.0],
      [38.0, -121.0], [42.0, -123.0], [45.0, -122.8], [47.6, -122.3] // Seattle
    ],
    stage5: [
      [47.6, -122.3], // Seattle
      [48.5, -125.0], [50.0, -128.0], [52.0, -132.0], [54.0, -136.0], [56.0, -140.0],
      [58.0, -145.0], [60.0, -150.0], [61.0, -155.0], [62.0, -160.0], [63.0, -165.0],
      [64.0, -168.0], // Alaska coast
      [65.5, -168.0], [66.0, -166.0], // Bering Strait area
      [67.0, -164.0], [68.0, -160.0], [69.0, -155.0], [70.0, -150.0], // Beaufort Sea
      [70.5, -145.0], [71.0, -140.0], [71.5, -135.0], [72.0, -130.0], // Arctic Canada
      [72.5, -125.0], [73.0, -120.0], [73.5, -115.0], [74.0, -110.0], // M'Clure Strait
      [74.2, -105.0], [74.0, -100.0], [73.8, -95.0], [73.5, -90.0], // Viscount Melville Sound
      [73.0, -85.0], [72.5, -80.0], [72.0, -75.0], [71.5, -70.0], // Barrow Strait
      [71.0, -65.0], [70.5, -60.0], [70.0, -55.0], // Lancaster Sound
      [69.0, -50.0], [68.0, -48.0], [67.0, -46.0], [66.0, -45.0], // Baffin Bay
      [65.0, -47.0], [64.5, -49.0], [64.2, -51.7] // Greenland
    ],
    stage6: [
      [64.2, -51.7], // Greenland
      [62.0, -48.0], [60.0, -44.0], [58.0, -40.0], [56.0, -36.0], [54.0, -32.0],
      [52.0, -28.0], [50.0, -24.0], [48.0, -20.0], [46.0, -16.0], [44.0, -12.0],
      [42.0, -8.0], [40.0, -4.0], [38.5, -1.0], [38.0, -0.7] // Return to Spain
    ]
  };

  // Waypoint markers for important locations
  const waypoints = [
    { name: 'Cabo Roig, Spain', position: [38.0, -0.7] as [number, number], type: 'start' },
    { name: 'Las Palmas, Gran Canaria', position: [28.1, -15.4] as [number, number], type: 'checkpoint' },
    { name: 'Caribbean', position: [14.7, -61.2] as [number, number], type: 'checkpoint' },
    { name: 'Panama Canal', position: [9.0, -79.8] as [number, number], type: 'checkpoint' },
    { name: 'Seattle, USA', position: [47.6, -122.3] as [number, number], type: 'checkpoint' },
    { name: 'Alaska Coast', position: [64.0, -168.0] as [number, number], type: 'checkpoint' },
    { name: 'Bering Strait', position: [66.0, -166.0] as [number, number], type: 'checkpoint' },
    { name: 'Arctic Canada', position: [74.0, -110.0] as [number, number], type: 'checkpoint' },
    { name: 'Lancaster Sound', position: [70.0, -55.0] as [number, number], type: 'checkpoint' },
    { name: 'Greenland', position: [64.2, -51.7] as [number, number], type: 'checkpoint' },
    { name: 'Return to Spain', position: [38.0, -0.7] as [number, number], type: 'finish' }
  ];

  return (
    <TrackingContainer>
      <BackgroundEffects />
      
      <Title>Our journey consists of the following stages</Title>
      <Subtitle>Follow Arctic Owl's Epic Journey Around North America</Subtitle>

      <AISStatus>
        {isLoading ? (
          <div style={{ color: '#718096' }}>
            🌐 Connecting to AIS transponder for Arctic Owl...
          </div>
        ) : error ? (
          <div style={{ color: '#e53e3e' }}>
            ❌ {error} - Using simulation data
          </div>
        ) : aisData ? (
          <div style={{ color: '#1a365d' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              🛰️ Live AIS Data: {aisData.shipName} - {aisData.status}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>
              Position: {aisData.latitude.toFixed(4)}°N, {Math.abs(aisData.longitude).toFixed(4)}°W | 
              Speed: {aisData.speed.toFixed(1)} knots | 
              Course: {aisData.course.toFixed(0)}° | 
              Updated: {new Date(aisData.timestamp).toLocaleTimeString()}
              {aisData.mmsi && ` | MMSI: ${aisData.mmsi}`}
            </div>
          </div>
        ) : (
          <div style={{ color: '#e53e3e' }}>
            ❌ AIS signal unavailable
          </div>
        )}
      </AISStatus>

      <MapWrapper>
        <GradientOverlay />
        
        <StageButtons>
          {stages.map((stage) => (
            <StageButton 
              key={stage.id} 
              isActive={currentStage === stage.id}
              onClick={() => setCurrentStage(stage.id)}
            >
              Stage {stage.id}
            </StageButton>
          ))}
        </StageButtons>
        
        <MapContainer_>
          <MapContainer
            center={aisData ? [aisData.latitude, aisData.longitude] : [28.1, -15.4]}
            zoom={3}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            
            {/* Route lines based on current stage */}
            {currentStage >= 1 && (
              <Polyline
                positions={seaRoutes.stage1}
                pathOptions={{ color: '#3182ce', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            {currentStage >= 2 && (
              <Polyline
                positions={seaRoutes.stage2}
                pathOptions={{ color: '#4299e1', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            {currentStage >= 3 && (
              <Polyline
                positions={seaRoutes.stage3}
                pathOptions={{ color: '#63b3ed', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            {currentStage >= 4 && (
              <Polyline
                positions={seaRoutes.stage4}
                pathOptions={{ color: '#90cdf4', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            {currentStage >= 5 && (
              <Polyline
                positions={seaRoutes.stage5}
                pathOptions={{ color: '#bde0fe', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            {currentStage >= 6 && (
              <Polyline
                positions={seaRoutes.stage6}
                pathOptions={{ color: '#c3daf7', weight: 5, opacity: 0.9, dashArray: '15, 8' }}
              />
            )}
            
            {/* Waypoint markers */}
            {waypoints.map((waypoint, index) => {
              // Show waypoint based on current stage
              let showWaypoint = false;
              if (waypoint.type === 'start' || waypoint.type === 'finish') {
                showWaypoint = true;
              } else if (index === 1 && currentStage >= 1) { // Gran Canaria
                showWaypoint = true;
              } else if (index === 2 && currentStage >= 2) { // Caribbean
                showWaypoint = true;
              } else if (index === 3 && currentStage >= 3) { // Panama Canal
                showWaypoint = true;
              } else if (index === 4 && currentStage >= 4) { // Seattle
                showWaypoint = true;
              } else if (index === 5 && currentStage >= 5) { // Alaska Coast
                showWaypoint = true;
              } else if (index === 6 && currentStage >= 5) { // Bering Strait
                showWaypoint = true;
              } else if (index === 7 && currentStage >= 5) { // Arctic Canada
                showWaypoint = true;
              } else if (index === 8 && currentStage >= 5) { // Lancaster Sound
                showWaypoint = true;
              } else if (index === 9 && currentStage >= 5) { // Greenland
                showWaypoint = true;
              }
              
              if (!showWaypoint) return null;
              
              const getMarkerColor = () => {
                if (waypoint.type === 'start') return '🏁';
                if (waypoint.type === 'finish') return '🏆';
                return '📍';
              };
              
              return (
                <Marker key={index} position={waypoint.position}>
                  <Popup>
                    <div style={{ color: '#1a365d', fontWeight: 'bold', textAlign: 'center' }}>
                      {getMarkerColor()} {waypoint.name}<br/>
                      <small style={{ color: '#4a5568' }}>
                        {waypoint.position[0].toFixed(2)}°N, {Math.abs(waypoint.position[1]).toFixed(2)}°W
                      </small>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            
            {/* Live AIS position */}
            {aisData && (
              <Marker position={[aisData.latitude, aisData.longitude]}>
                <Popup>
                  <div style={{ color: '#1a365d', fontWeight: 'bold' }}>
                    ⛵ {aisData.shipName} - LIVE<br/>
                    Speed: {aisData.speed.toFixed(1)} knots<br/>
                    Course: {aisData.course.toFixed(0)}°<br/>
                    Status: {aisData.status}
                    <br/><small style={{ color: '#4a5568' }}>
                      Last update: {new Date(aisData.timestamp).toLocaleString()}
                    </small>
                  </div>
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </MapContainer_>
        
        <InfoPanel>
          <h3 style={{
            fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
            marginBottom: '1rem',
            color: '#3182ce',
            fontWeight: 300,
            letterSpacing: '1px'
          }}>
            {stages[currentStage - 1].title}
          </h3>
          
          <p style={{
            lineHeight: 1.8,
            marginBottom: '1.5rem',
            color: '#4a5568',
            fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)'
          }}>
            {stages[currentStage - 1].description}
          </p>
          
          <InfoGrid>
            {['route', 'duration', 'distance'].map((key, index) => (
              <InfoCard key={key}>
                <InfoCardLabel>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </InfoCardLabel>
                <InfoCardValue>
                  {stages[currentStage - 1][key as keyof typeof stages[0]]}
                </InfoCardValue>
              </InfoCard>
            ))}
          </InfoGrid>

          {/* Tiny gallery as a card to match styling */}
          <InfoGrid>
            <InfoCard>
              <InfoCardLabel>Gallery</InfoCardLabel>
              {(() => {
                const id = stages[currentStage - 1].id;
                const images = [1,2,3,4,5].map(n => `/images/stages/stage${id}/0${n}.jpeg`);
                return <TinyGallery images={images} />;
              })()}
            </InfoCard>
          </InfoGrid>
        </InfoPanel>
      </MapWrapper>
    </TrackingContainer>
  );
};

export default Tracking; 
